import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { EyeSlash, Eye, ArrowLeft, Info } from "@phosphor-icons/react";

// Plausible exclusion page. Uses Plausible's official opt-out mechanism:
// setting `localStorage.plausible_ignore = "true"` makes the tracking script
// silently skip every event from this browser. The setting is stored per
// origin+device — it must be enabled on every browser/device used for tests.
export default function PlausibleOptout() {
  const [ignored, setIgnored] = useState(false);

  useEffect(() => {
    try {
      setIgnored(localStorage.getItem("plausible_ignore") === "true");
    } catch {}
  }, []);

  const enable = () => {
    try {
      localStorage.setItem("plausible_ignore", "true");
      setIgnored(true);
    } catch {}
  };

  const disable = () => {
    try {
      localStorage.removeItem("plausible_ignore");
      setIgnored(false);
    } catch {}
  };

  return (
    <div className="min-h-screen bg-surface px-5 py-10">
      <div className="max-w-2xl mx-auto space-y-8">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-ink font-sans transition-colors"
          data-testid="plausible-optout-back"
        >
          <ArrowLeft size={14} weight="bold" /> Torna al pannello admin
        </Link>

        <div>
          <p className="text-xs uppercase tracking-widest text-terracotta font-semibold font-sans">Admin · Analytics</p>
          <h1 className="font-display font-black text-3xl text-ink mt-1 tracking-tight">
            Escludi questo dispositivo da Plausible
          </h1>
          <p className="text-sm text-ink-soft leading-relaxed font-sans mt-3">
            Attivando l'opt-out, tutti gli eventi generati da <strong>questo browser su questo dispositivo</strong> vengono ignorati da Plausible. Il tuo test personale non inquina più le metriche di produzione (funnel, pageview, conversioni).
          </p>
        </div>

        <div
          className={`rounded-2xl border p-6 flex items-start gap-4 ${ignored ? "border-sage/40 bg-sage/5" : "border-border bg-surface-card"}`}
          data-testid="plausible-optout-status"
          data-ignored={ignored ? "true" : "false"}
        >
          <div className={`h-12 w-12 shrink-0 rounded-2xl flex items-center justify-center ${ignored ? "bg-sage/20" : "bg-ink-soft/10"}`}>
            {ignored
              ? <EyeSlash size={22} weight="duotone" className="text-sage" />
              : <Eye size={22} weight="duotone" className="text-ink-soft" />}
          </div>
          <div className="flex-1 space-y-1">
            <div className="font-display font-black text-lg text-ink">
              {ignored ? "Opt-out attivo su questo dispositivo" : "Tracciamento attivo su questo dispositivo"}
            </div>
            <p className="text-sm text-ink-soft font-sans leading-relaxed">
              {ignored
                ? "I tuoi eventi Plausible su exchangeplan.app NON vengono registrati. Puoi riattivarli in qualsiasi momento."
                : "I tuoi eventi Plausible su exchangeplan.app vengono registrati normalmente."}
            </p>
          </div>
          {ignored ? (
            <Button
              data-testid="plausible-optout-disable"
              onClick={disable}
              variant="outline"
              className="rounded-full border-ink-soft/30 text-ink hover:bg-ink/5 font-sans font-semibold shrink-0"
            >
              Disattiva opt-out
            </Button>
          ) : (
            <Button
              data-testid="plausible-optout-enable"
              onClick={enable}
              className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white font-sans font-semibold shrink-0"
            >
              Attiva opt-out
            </Button>
          )}
        </div>

        <div className="rounded-xl border border-sand/50 bg-sand/10 p-4 flex gap-3">
          <Info size={18} weight="duotone" className="text-terracotta shrink-0 mt-0.5" />
          <div className="text-sm text-ink font-sans leading-relaxed space-y-2">
            <p>
              <strong>Importante:</strong> questa esclusione vale solo per <em>questo browser su questo dispositivo</em>. Non è un'impostazione globale del tuo account.
            </p>
            <p>
              Se testi da più device (es. desktop, laptop, telefono) o da browser diversi (es. Chrome + Safari + Firefox), devi <strong>ripetere l'attivazione su ognuno</strong>.
            </p>
            <p className="text-ink-soft">
              Se cancelli i dati del sito o usi la modalità in incognito, l'opt-out si azzera e va riattivato.
            </p>
          </div>
        </div>

        <details className="rounded-xl border border-border/60 bg-surface-card/50 p-4">
          <summary className="text-sm font-sans font-semibold text-ink cursor-pointer">Come funziona tecnicamente?</summary>
          <p className="text-sm text-ink-soft font-sans leading-relaxed mt-3">
            Salviamo <code className="font-mono text-xs bg-surface px-1.5 py-0.5 rounded">plausible_ignore=true</code> nel <code className="font-mono text-xs bg-surface px-1.5 py-0.5 rounded">localStorage</code> di questo browser. Il tracking script di Plausible controlla questa chiave a ogni evento e, se presente, non invia nulla al server. È il meccanismo ufficiale documentato da Plausible.
          </p>
        </details>
      </div>
    </div>
  );
}
